import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8;
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { RemoteError } from "abweb/dto/exception/RemoteError";
import * as AbWebResources from "abweb/generated/locale/AbWebResources";
import { AbWebServices } from "abweb/service/AbWebServices";
import { FailedToFetchError } from "abweb/service/utils/rpc/FailedToFetchError";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { ErrorUtils } from "abweb/utils/ErrorUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { action, computed, observable, runInAction, makeObservable } from "mobx";
const LOG = LogUtils.getLogger(__filename);
export let AbLoginDialogMachine = (_class = class AbLoginDialogMachine {
  constructor(productInfo, successUrl) {
    _initializerDefineProperty(this, "errorMessage", _descriptor, this);
    _initializerDefineProperty(this, "productNameSupported", _descriptor2, this);
    _initializerDefineProperty(this, "username", _descriptor3, this);
    _initializerDefineProperty(this, "password", _descriptor4, this);
    _initializerDefineProperty(this, "loginEnabled", _descriptor5, this);
    _initializerDefineProperty(this, "_productInfo", _descriptor6, this);
    /**
     * If successUrl is defined, login will redirect to it after successful login.
     * If it undefined, the server will redirect to the last failed auth request on successful login.
     */
    _defineProperty(this, "_successUrl", void 0);
    _initializerDefineProperty(this, "_isBusy", _descriptor7, this);
    _initializerDefineProperty(this, "_isClosed", _descriptor8, this);
    makeObservable(this);
    this.errorMessage = undefined;
    this.username = "";
    this.password = "";
    this.loginEnabled = true;
    this.productNameSupported = true;
    this._isBusy = false;
    this._isClosed = false;
    this._productInfo = productInfo;
    this._successUrl = successUrl;
  }
  get isClosed() {
    return this._isClosed;
  }
  get isBusy() {
    return this._isBusy;
  }
  get productInfo() {
    return this._productInfo;
  }
  get productName() {
    return this.productInfo.name;
  }
  async login() {
    if (!this.validateUsername()) {
      this.errorMessage = AbWebResources.loginDlg_invalidUsernameOrPasswordError();
      return false;
    }
    this._isBusy = true;
    this.errorMessage = null;
    let success = false;
    try {
      success = await this.doLogin();
      if (success) {
        runInAction(() => {
          this._isClosed = true;
        });
      }
    } catch (e) {
      runInAction(() => {
        this.errorMessage = e.message;
      });
    }
    runInAction(() => {
      this._isBusy = false;
    });
    return success;
  }
  validateUsername() {
    return this.username.trim().length > 0;
  }
  async doLogin() {
    try {
      // Note rpc 403 response cause below to fail but to return the current (anonymous) user - no exception is thrown
      const userInfo = await AbWebServices.authenticationService.login(this.username, this.password, true, this._successUrl);
      if (userInfo) {
        AbWebServices.sessionData.set("currentUser", userInfo);
      }

      // Success if authenticated user (aka is not anonymous)
      if (!AbWebServices.sessionData.currentUser.isAnonymous) {
        return true;
      }
      // Else authentication failed gracefully (returned anonymous user)
      runInAction(() => {
        this.errorMessage = AbWebResources.loginDlg_invalidUsernameOrPasswordError();
        this.password = "";
      });
      return false;
    } catch (error) {
      // Authentication failed ungracefully!   Typically via a 500 exception thrown from server

      if (ClassUtils.isInstanceOf(error, FailedToFetchError)) {
        // Can't reach the server (most likely: server or network is down).
        // See FailedToFetchError for list of known conditions where this error is thrown by Fetch.
        // Do NOT have access to a response at all as Fetch throws before returning a response.
        runInAction(() => {
          this.errorMessage = AbWebResources.loginDlg_serverUnreachable();
          this.password = "";
          this.loginEnabled = true;
        });
      } else if (ClassUtils.isInstanceOf(error, RemoteError)) {
        const remoteErr = error;
        const remoteEx = remoteErr.remoteException;
        function defaultErrMsg() {
          return remoteEx?.message ?? ErrorUtils.getErrorMsgFromObj(remoteErr) // probably remoteError.message
          ?? AbWebResources.loginDlg_failedAuthenticationOrAuthorizationMessage();
        }

        // Handle 'loginFailed*' errors created by java SecurityHelper::createAbInitioErrorException
        if (remoteEx?.messageCode?.startsWith("login.failed"))
          // Do NOT expect login dialog to ever receive "authorization.noSession" exception
          // as that is thrown in sso authentication workflows
          {
            /*
             // SecurityHelper::createAbInitioErrorException sends showLogin and showError as part of exception msgArgs.
             // Index.jsp will act upon this showLogin/showError metadata in the caught exceptions,
             // as the index.jsp can be in sso and 'normal' workflow where sometimes the
             // login dlg or the errors messages within it should not be displayed.
             // However, this AbLoginDialogMachine workflow always is in conjunction with react
             // login dialog (not invoked on sso workflows), so this code always treats showLogin=showError=true (regardless of
             // incoming messageArg values).
              // Rob believes following two always true for SecurityHelper RemoteError, but defensively guard to avoid NPE
              if guard above has additional clause: '&& remoteEx.messageArgs?.length && (remoteEx.messageArgs.length > 3))'
              const showLogin: boolean = remoteEx.messageArgs[2] !== "false";
              const showError: boolean = remoteEx.messageArgs[3] !== "false";
              // previous behavior, do not understand as authentication failed so why return true? AY/RF do not know why.
              if (showError) return true;
            */
            let errMsg;
            const msgCode = remoteErr.remoteException?.messageCode;
            switch (msgCode) {
              case "login.failedLoginMessage":
                errMsg = AbWebResources.loginDlg_invalidUsernameOrPasswordError();
                break;
              case "login.failedAuthorizationMessage":
                errMsg = AbWebResources.accessDeniedDlg_error();
                break;
              case "login.failedAuthenticationOrAuthorizationMessage":
                // do not expect authorization.noSession to be hit as short circuits on showError=false above,
                // and only issues for sso connections
                errMsg = AbWebResources.loginDlg_failedAuthenticationOrAuthorizationMessage();
                break;
              default:
                errMsg = defaultErrMsg();
                LOG.error(`Unknown failed login messageCode returned from server: ${msgCode}.  Defaulting errMsg to: '${errMsg}'`);
                break;
            }
            runInAction(() => {
              this.errorMessage = errMsg;
              this.password = "";
              this.loginEnabled = true; // = showLogin;
            });
          } else {
          // Only supported case where we get here is when remoteErr.isAuthenticationServiceMisconfiguredError,
          // where the message is already localized, see MH-22049
          const errMsg = defaultErrMsg(); // fallback
          if (!remoteErr.isAuthenticationServiceMisconfiguredError) {
            LOG.error("Unknown type of error during login dialog: " + errMsg);
          }
          runInAction(() => {
            this.errorMessage = errMsg;
            this.password = "";
            this.loginEnabled = true;
          });
        }
      } else {
        // Else we have no idea went wrong.  This is the fallback of last resort!
        // So to give user some indication, we'll show the error.message on the login dialog
        // Otherwise user will repeatedly keep trying on a login dialog that is providing no feedback - frustrating!
        const errMsg = ErrorUtils.getErrorMsgFromObj(error) ?? AbWebResources.loginDlg_failedAuthenticationOrAuthorizationMessage(); // fallback;
        LOG.error("Unknown type of error during login dialog: " + errMsg);
        runInAction(() => {
          this.errorMessage = errMsg;
          this.password = "";
          this.loginEnabled = true;
        });
      }
      return false;
    }
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "errorMessage", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "productNameSupported", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "username", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "password", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "loginEnabled", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "_productInfo", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "_isBusy", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "_isClosed", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "productName", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "productName"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "login", [action], Object.getOwnPropertyDescriptor(_class.prototype, "login"), _class.prototype), _class);
//# sourceMappingURL=AbLoginDialogMachine.js.map
